// src/pages/Convocations/components/ConvocationsFilters.jsx

import React, {
  useState,
} from "react";

import {
  FaCalendarAlt,
  FaChevronDown,
  FaChevronUp,
  FaFilter,
  FaSearch,
  FaSlidersH,
  FaTimes,
} from "react-icons/fa";

import {
  RESPONSE_STATUS_OPTIONS,
  SORT_OPTIONS,
} from "../data/convocation.data";

import {
  cx,
  getActorLabel,
} from "../utils/convocation.utils";

const fieldClassName = [
  "h-11 w-full",
  "rounded-xl",
  "border border-slate-200",
  "bg-white",
  "px-3",
  "text-sm font-semibold",
  "text-slate-800",
  "outline-none",
  "transition",
  "placeholder:text-slate-400",
  "focus:border-blue-500",
  "focus:ring-4",
  "focus:ring-blue-500/10",
  "dark:border-slate-700",
  "dark:bg-slate-950",
  "dark:text-white",
  "dark:placeholder:text-slate-500",
].join(" ");

function FilterField({
  label,
  children,
  className = "",
}) {
  return (
    <div className={className}>
      <label
        className="
          mb-1.5 block
          text-[10px] font-black
          uppercase
          tracking-[0.12em]
          text-slate-500
          dark:text-slate-400
        "
      >
        {label}
      </label>

      {children}
    </div>
  );
}

export default function ConvocationsFilters({
  filters,
  actors,
  typeOptions,
  posteOptions,
  activeFilterCount,
  resultCount,
  onChange,
  onReset,
}) {
  const [
    mobileFiltersOpen,
    setMobileFiltersOpen,
  ] = useState(false);

  const safeActors = Array.isArray(
    actors,
  )
    ? actors
    : [];

  return (
    <section
      className="
        overflow-hidden
        rounded-3xl
        border border-slate-200/80
        bg-white/90
        shadow-[0_22px_65px_-40px_rgba(15,23,42,0.5)]
        backdrop-blur
        dark:border-slate-800
        dark:bg-slate-900/90
      "
    >
      <div
        className="
          flex items-center
          justify-between gap-3
          border-b border-slate-100
          px-4 py-4
          dark:border-slate-800
          sm:px-5
        "
      >
        <div className="min-w-0">
          <div
            className="
              flex items-center gap-2
              text-sm font-black
              text-slate-900
              dark:text-white
            "
          >
            <span
              className="
                flex h-9 w-9
                shrink-0 items-center
                justify-center
                rounded-xl
                bg-blue-50
                text-blue-700
                dark:bg-blue-950/50
                dark:text-blue-300
              "
            >
              <FaSlidersH />
            </span>

            Filtres et recherche

            {activeFilterCount > 0 ? (
              <span
                className="
                  inline-flex min-h-5
                  min-w-5 items-center
                  justify-center
                  rounded-full
                  bg-blue-600
                  px-1.5
                  text-[10px] font-semibold
                  text-white
                "
              >
                {activeFilterCount}
              </span>
            ) : null}
          </div>

          <p
            className="
              mt-1 text-xs
              font-semibold
              text-slate-500
              dark:text-slate-400
            "
          >
            {resultCount} convocation
            {resultCount > 1
              ? "s"
              : ""}{" "}
            affichée
            {resultCount > 1
              ? "s"
              : ""}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {activeFilterCount > 0 ? (
            <button
              type="button"
              onClick={onReset}
              className="
                hidden min-h-10
                items-center gap-2
                rounded-xl
                border border-slate-200
                bg-white px-3
                text-xs font-semibold
                text-slate-600
                transition
                hover:bg-slate-50
                hover:text-rose-600
                dark:border-slate-700
                dark:bg-slate-950
                dark:text-slate-300
                dark:hover:bg-slate-800
                sm:inline-flex
              "
            >
              <FaTimes />

              Réinitialiser
            </button>
          ) : null}

          <button
            type="button"
            onClick={() =>
              setMobileFiltersOpen(
                (currentValue) =>
                  !currentValue,
              )
            }
            aria-expanded={
              mobileFiltersOpen
            }
            className="
              inline-flex min-h-10
              items-center gap-2
              rounded-xl
              bg-slate-900
              px-3
              text-xs font-semibold
              text-white
              transition
              active:scale-[0.98]
              dark:bg-white
              dark:text-slate-900
              lg:hidden
            "
          >
            <FaFilter />

            Filtres

            {mobileFiltersOpen ? (
              <FaChevronUp />
            ) : (
              <FaChevronDown />
            )}
          </button>
        </div>
      </div>

      <div
        className={cx(
          "p-4 sm:p-5",
          mobileFiltersOpen
            ? "block"
            : "hidden",
          "lg:block",
        )}
      >
        <div
          className="
            grid grid-cols-1
            gap-4
            md:grid-cols-2
            xl:grid-cols-12
          "
        >
          <FilterField
            label="Rechercher"
            className="xl:col-span-4"
          >
            <div className="relative">
              <FaSearch
                className="
                  pointer-events-none
                  absolute left-3
                  top-1/2
                  -translate-y-1/2
                  text-sm
                  text-slate-400
                "
              />

              <input
                type="search"
                value={
                  filters.searchQuery
                }
                onChange={(event) =>
                  onChange(
                    "searchQuery",
                    event.target.value,
                  )
                }
                placeholder="Événement, lieu, poste, tapis..."
                className={cx(
                  fieldClassName,
                  "pl-9",
                )}
              />
            </div>
          </FilterField>

          <FilterField
            label="Statut"
            className="xl:col-span-2"
          >
            <select
              value={
                filters.selectedStatus
              }
              onChange={(event) =>
                onChange(
                  "selectedStatus",
                  event.target.value,
                )
              }
              className={fieldClassName}
            >
              {RESPONSE_STATUS_OPTIONS.map(
                (option) => (
                  <option
                    key={
                      option.value ||
                      "all"
                    }
                    value={option.value}
                  >
                    {option.label}
                  </option>
                ),
              )}
            </select>
          </FilterField>

          <FilterField
            label="Type d’événement"
            className="xl:col-span-2"
          >
            <select
              value={
                filters.selectedType
              }
              onChange={(event) =>
                onChange(
                  "selectedType",
                  event.target.value,
                )
              }
              className={fieldClassName}
            >
              <option value="">
                Tous les types
              </option>

              {typeOptions.map(
                (type) => (
                  <option
                    key={type}
                    value={type}
                  >
                    {type}
                  </option>
                ),
              )}
            </select>
          </FilterField>

          <FilterField
            label="Poste"
            className="xl:col-span-2"
          >
            <select
              value={
                filters.selectedPoste
              }
              onChange={(event) =>
                onChange(
                  "selectedPoste",
                  event.target.value,
                )
              }
              className={fieldClassName}
            >
              <option value="">
                Tous les postes
              </option>

              {posteOptions.map(
                (poste) => (
                  <option
                    key={poste}
                    value={poste}
                  >
                    {poste}
                  </option>
                ),
              )}
            </select>
          </FilterField>

          <FilterField
            label="Trier par"
            className="xl:col-span-2"
          >
            <select
              value={filters.sortBy}
              onChange={(event) =>
                onChange(
                  "sortBy",
                  event.target.value,
                )
              }
              className={fieldClassName}
            >
              {SORT_OPTIONS.map(
                (option) => (
                  <option
                    key={option.value}
                    value={option.value}
                  >
                    {option.label}
                  </option>
                ),
              )}
            </select>
          </FilterField>
        </div>

        <div
          className="
            mt-4 flex flex-col
            gap-3
            border-t border-slate-100
            pt-4
            dark:border-slate-800
            sm:flex-row
            sm:items-end
            sm:justify-between
          "
        >
          <div
            className="
              grid flex-1
              grid-cols-1 gap-3
              sm:grid-cols-2
            "
          >
            {safeActors.length > 1 ? (
              <FilterField label="Profil acteur">
                <select
                  value={
                    filters.selectedActorId
                  }
                  onChange={(event) =>
                    onChange(
                      "selectedActorId",
                      event.target.value,
                    )
                  }
                  className={fieldClassName}
                >
                  <option value="">
                    Tous les profils
                  </option>

                  {safeActors.map(
                    (actor) => (
                      <option
                        key={actor.id}
                        value={actor.id}
                      >
                        {getActorLabel(
                          actor,
                        )}
                      </option>
                    ),
                  )}
                </select>
              </FilterField>
            ) : null}

            <div>
              <p
                className="
                  mb-1.5
                  text-[10px] font-semibold
                  uppercase
                  tracking-[0.12em]
                  text-slate-500
                  dark:text-slate-400
                "
              >
                Période affichée
              </p>

              <button
                type="button"
                onClick={() =>
                  onChange(
                    "showOnlyFuture",
                    !filters.showOnlyFuture,
                  )
                }
                aria-pressed={
                  filters.showOnlyFuture
                }
                className={cx(
                  [
                    "flex h-11 w-full",
                    "items-center justify-between",
                    "gap-3 rounded-xl",
                    "border px-3",
                    "text-left",
                    "text-xs font-semibold",
                    "transition",
                    "sm:max-w-[310px]",
                  ].join(" "),
                  filters.showOnlyFuture
                    ? [
                        "border-blue-200",
                        "bg-blue-50",
                        "text-blue-700",
                        "dark:border-blue-800",
                        "dark:bg-blue-950/40",
                        "dark:text-blue-300",
                      ].join(" ")
                    : [
                        "border-slate-200",
                        "bg-white",
                        "text-slate-600",
                        "dark:border-slate-700",
                        "dark:bg-slate-950",
                        "dark:text-slate-300",
                      ].join(" "),
                )}
              >
                <span className="inline-flex items-center gap-2">
                  <FaCalendarAlt />

                  {filters.showOnlyFuture
                    ? "Événements à venir"
                    : "Avec les événements passés"}
                </span>

                <span
                  className={cx(
                    [
                      "relative h-6 w-11",
                      "shrink-0 rounded-full",
                      "transition",
                    ].join(" "),
                    filters.showOnlyFuture
                      ? "bg-blue-600"
                      : "bg-slate-300 dark:bg-slate-700",
                  )}
                >
                  <span
                    className={cx(
                      [
                        "absolute top-1",
                        "h-4 w-4",
                        "rounded-full",
                        "bg-white",
                        "shadow",
                        "transition-transform",
                      ].join(" "),
                      filters.showOnlyFuture
                        ? "translate-x-6"
                        : "translate-x-1",
                    )}
                  />
                </span>
              </button>
            </div>
          </div>

          {activeFilterCount > 0 ? (
            <button
              type="button"
              onClick={onReset}
              className="
                inline-flex min-h-11
                w-full items-center
                justify-center gap-2
                rounded-xl
                border border-rose-200
                bg-rose-50
                px-4
                text-xs font-semibold
                text-rose-700
                transition
                hover:bg-rose-100
                dark:border-rose-900/70
                dark:bg-rose-950/30
                dark:text-rose-300
                dark:hover:bg-rose-950/50
                sm:hidden
              "
            >
              <FaTimes />

              Réinitialiser les filtres
            </button>
          ) : null}
        </div>
      </div>
    </section>
  );
}